import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaPlusCircle, FaGlobe, FaWordpress, FaServer, FaLock, FaSearch } from 'react-icons/fa';
import config from '../config';

interface Lead {
  id: number;
  name: string;
}

interface ClientWebsite {
  id: number;
  lead_id: number;
  lead_name: string;
  website_title: string;
  domain_name: string;
  site_url: string;
  wp_admin_url: string;
  wp_username: string;
  hosting_provider: string;
  hosting_login_url: string;
  hosting_username: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  id?: number;
  lead_id: number;
  website_title: string;
  domain_name: string;
  site_url: string;
  wp_admin_url: string;
  wp_username: string;
  wp_password: string;
  hosting_provider: string;
  hosting_login_url: string;
  hosting_username: string;
  hosting_password: string;
  notes: string;
}

const initialFormState: FormData = {
  lead_id: 0,
  website_title: '',
  domain_name: '',
  site_url: '',
  wp_admin_url: '',
  wp_username: '',
  wp_password: '',
  hosting_provider: '',
  hosting_login_url: '',
  hosting_username: '',
  hosting_password: '',
  notes: ''
};

const ClientWebsites: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [currentWebsiteId, setCurrentWebsiteId] = useState<number | null>(null);
  const [expandedWebsite, setExpandedWebsite] = useState<number | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({
    wp_password: false,
    hosting_password: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  // Fetch client websites
  const { data: websites, isLoading: websitesLoading, error: websitesError } = useQuery<{ records: ClientWebsite[] }>({
    queryKey: ['client-websites'],
    queryFn: async () => {
      const response = await axios.get(`${config.apiBaseUrl}/client_websites/read.php`);
      return response.data;
    }
  });

  // Fetch leads for the dropdown
  const { data: leads, isLoading: leadsLoading } = useQuery<{ records: Lead[] }>({
    queryKey: ['leads-basic'],
    queryFn: async () => {
      const response = await axios.get(`${config.apiBaseUrl}/leads/read_basic.php`);
      return response.data;
    }
  });

  // Mutation for creating a website
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await axios.post(`${config.apiBaseUrl}/client_websites/create.php`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-websites'] });
      setIsModalOpen(false);
      setFormData(initialFormState);
    }
  });

  // Mutation for updating a website
  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await axios.post(`${config.apiBaseUrl}/client_websites/update.php`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-websites'] });
      setIsModalOpen(false);
      setFormData(initialFormState);
    }
  });

  // Mutation for deleting a website
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await axios.post(`${config.apiBaseUrl}/client_websites/delete.php`, { id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-websites'] });
      setIsDeleteModalOpen(false);
      setCurrentWebsiteId(null);
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'lead_id' ? parseInt(value, 10) : value 
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentWebsiteId) {
      // Update existing website
      updateMutation.mutate({ ...formData, id: currentWebsiteId });
    } else {
      // Create new website
      createMutation.mutate(formData);
    }
  };

  const openEditModal = (website: ClientWebsite) => {
    setCurrentWebsiteId(website.id);
    setFormData({
      lead_id: website.lead_id,
      website_title: website.website_title,
      domain_name: website.domain_name || '',
      site_url: website.site_url || '',
      wp_admin_url: website.wp_admin_url || '',
      wp_username: website.wp_username || '',
      wp_password: '', // Don't pre-fill passwords for security
      hosting_provider: website.hosting_provider || '',
      hosting_login_url: website.hosting_login_url || '',
      hosting_username: website.hosting_username || '',
      hosting_password: '', // Don't pre-fill passwords for security
      notes: website.notes || ''
    });
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    setCurrentWebsiteId(null);
    setFormData(initialFormState);
    setIsModalOpen(true);
  };

  const openDeleteModal = (id: number) => {
    setCurrentWebsiteId(id);
    setIsDeleteModalOpen(true);
  };

  const togglePasswordVisibility = (field: 'wp_password' | 'hosting_password') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const toggleExpand = (id: number) => {
    setExpandedWebsite(expandedWebsite === id ? null : id);
  };

  // Filter websites based on search term
  const filteredWebsites = websites?.records.filter(website => {
    const searchString = searchTerm.toLowerCase();
    return (
      website.website_title.toLowerCase().includes(searchString) ||
      website.lead_name.toLowerCase().includes(searchString) ||
      website.domain_name?.toLowerCase().includes(searchString) ||
      website.hosting_provider?.toLowerCase().includes(searchString)
    );
  });

  if (websitesLoading || leadsLoading) return <div className="p-4">Loading client websites...</div>;
  if (websitesError) return <div className="p-4 text-red-500">Error loading websites: {(websitesError as Error).message}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Client Websites</h2>
        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
        >
          <FaPlusCircle /> Add Website
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
            placeholder="Search websites by title, client, domain, or hosting..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Websites list */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {!websites?.records.length ? (
          <p className="p-6 text-center text-gray-500">No client websites found. Create one to get started.</p>
        ) : !filteredWebsites?.length ? (
          <p className="p-6 text-center text-gray-500">No websites match your search criteria.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredWebsites?.map(website => (
              <li key={website.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{website.website_title}</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      <strong>Client:</strong> {website.lead_name}
                    </p>
                    
                    <div className="mt-2 flex flex-wrap gap-2">
                      {website.site_url && (
                        <a 
                          href={website.site_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <FaGlobe className="mr-1" /> Visit Site
                        </a>
                      )}
                      
                      {website.wp_admin_url && (
                        <a 
                          href={website.wp_admin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100"
                        >
                          <FaWordpress className="mr-1" /> WP Admin
                        </a>
                      )}
                      
                      {website.hosting_login_url && (
                        <a 
                          href={website.hosting_login_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100"
                        >
                          <FaServer className="mr-1" /> Hosting
                        </a>
                      )}
                    </div>
                    
                    {/* Collapsible details */}
                    <button
                      onClick={() => toggleExpand(website.id)}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-800"
                    >
                      {expandedWebsite === website.id ? 'Hide details' : 'Show details'}
                    </button>
                    
                    {expandedWebsite === website.id && (
                      <div className="mt-4 space-y-3 bg-gray-50 p-4 rounded-md">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium">Website Details</h4>
                            <ul className="mt-1 space-y-1 text-sm">
                              <li><strong>Domain:</strong> {website.domain_name || 'N/A'}</li>
                              <li><strong>Created:</strong> {new Date(website.created_at).toLocaleDateString()}</li>
                              <li><strong>Last Updated:</strong> {new Date(website.updated_at).toLocaleDateString()}</li>
                              <li><strong>WP Username:</strong> {website.wp_username || 'N/A'}</li>
                              <li className="flex items-center">
                                <strong>WP Password:</strong>
                                <span className="text-gray-500 italic ml-1">Hidden for security</span>
                                <FaLock className="ml-2 text-gray-400" />
                              </li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium">Hosting Details</h4>
                            <ul className="mt-1 space-y-1 text-sm">
                              <li><strong>Provider:</strong> {website.hosting_provider || 'N/A'}</li>
                              <li><strong>Username:</strong> {website.hosting_username || 'N/A'}</li>
                              <li className="flex items-center">
                                <strong>Password:</strong>
                                <span className="text-gray-500 italic ml-1">Hidden for security</span>
                                <FaLock className="ml-2 text-gray-400" />
                              </li>
                            </ul>
                          </div>
                        </div>
                        
                        {website.notes && (
                          <div>
                            <h4 className="font-medium">Notes</h4>
                            <p className="text-sm mt-1">{website.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(website)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                      title="Edit website"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => openDeleteModal(website.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      title="Delete website"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {currentWebsiteId ? 'Edit Website' : 'Add New Website'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="lead_id" className="block text-sm font-medium text-gray-700">
                        Client*
                      </label>
                      <select
                        id="lead_id"
                        name="lead_id"
                        value={formData.lead_id}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select a client</option>
                        {leads?.records.map(lead => (
                          <option key={lead.id} value={lead.id}>{lead.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="website_title" className="block text-sm font-medium text-gray-700">
                        Website Title*
                      </label>
                      <input
                        type="text"
                        id="website_title"
                        name="website_title"
                        value={formData.website_title}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="domain_name" className="block text-sm font-medium text-gray-700">
                        Domain Name
                      </label>
                      <input
                        type="text"
                        id="domain_name"
                        name="domain_name"
                        value={formData.domain_name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="site_url" className="block text-sm font-medium text-gray-700">
                        Site URL
                      </label>
                      <input
                        type="url"
                        id="site_url"
                        name="site_url"
                        value={formData.site_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* WordPress Details */}
                <div>
                  <h3 className="text-lg font-medium mb-3">WordPress Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="wp_admin_url" className="block text-sm font-medium text-gray-700">
                        WordPress Admin URL
                      </label>
                      <input
                        type="url"
                        id="wp_admin_url"
                        name="wp_admin_url"
                        value={formData.wp_admin_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_username" className="block text-sm font-medium text-gray-700">
                        WordPress Username
                      </label>
                      <input
                        type="text"
                        id="wp_username"
                        name="wp_username"
                        value={formData.wp_username}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_password" className="block text-sm font-medium text-gray-700">
                        WordPress Password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          type={showPasswords.wp_password ? "text" : "password"}
                          id="wp_password"
                          name="wp_password"
                          value={formData.wp_password}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={currentWebsiteId ? "Leave blank to keep current password" : ""}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() => togglePasswordVisibility('wp_password')}
                        >
                          {showPasswords.wp_password ? <span>Hide</span> : <span>Show</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hosting Details */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Hosting Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="hosting_provider" className="block text-sm font-medium text-gray-700">
                        Hosting Provider
                      </label>
                      <input
                        type="text"
                        id="hosting_provider"
                        name="hosting_provider"
                        value={formData.hosting_provider}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_login_url" className="block text-sm font-medium text-gray-700">
                        Hosting Login URL
                      </label>
                      <input
                        type="url"
                        id="hosting_login_url"
                        name="hosting_login_url"
                        value={formData.hosting_login_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_username" className="block text-sm font-medium text-gray-700">
                        Hosting Username
                      </label>
                      <input
                        type="text"
                        id="hosting_username"
                        name="hosting_username"
                        value={formData.hosting_username}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_password" className="block text-sm font-medium text-gray-700">
                        Hosting Password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          type={showPasswords.hosting_password ? "text" : "password"}
                          id="hosting_password"
                          name="hosting_password"
                          value={formData.hosting_password}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={currentWebsiteId ? "Leave blank to keep current password" : ""}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() => togglePasswordVisibility('hosting_password')}
                        >
                          {showPasswords.hosting_password ? <span>Hide</span> : <span>Show</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Website'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Delete Website</h2>
              <p className="mb-6">Are you sure you want to delete this client website? This action cannot be undone.</p>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => currentWebsiteId && deleteMutation.mutate(currentWebsiteId)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Website'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientWebsites; 